import { create } from 'zustand'
import { reduce, initialSession, type ControlSession, type ControlEvent } from '../services/remoteControl/sessionMachine'
import { publishControl } from '../services/remoteControl/channel'
import type { ControlPacket } from '../services/remoteControl/protocol'
import { useLivekitStore } from './livekitStore'
import { getSocket } from '../services/socket'
import { useToastStore } from './toastStore'

const REQUEST_TIMEOUT_MS = 30000
let requestTimer: ReturnType<typeof setTimeout> | null = null
function clearRequestTimer() {
  if (requestTimer) { clearTimeout(requestTimer); requestTimer = null }
}

interface IncomingPayload {
  sessionId: string
  fromUserId: string
  fromName: string
  controllerIdentity: string
  channelId: string
  serverId: string
}

interface IncomingCtx {
  channelId: string
  serverId: string
  controllerUserId: string
  controllerIdentity: string
}

interface RemoteControlState {
  session: ControlSession
  incomingCtx: IncomingCtx | null
  dispatch: (e: ControlEvent) => void
  reset: () => void

  // controllee side
  onIncoming: (p: IncomingPayload) => void
  approve: () => void
  deny: () => void

  // controller side
  request: (args: { channelId: string; serverId: string; controlleeUserId: string; controlleeName: string; controllerIdentity: string }) => void
  onDecision: (p: { sessionId: string; accepted: boolean }) => void
  sendInput: (targetIdentity: string, packet: ControlPacket) => void

  // shared
  end: (reason: string) => void
}

export const useRemoteControlStore = create<RemoteControlState>((set, get) => ({
  session: initialSession,
  incomingCtx: null,
  dispatch: (e) => set((s) => ({ session: reduce(s.session, e) })),
  reset: () => { clearRequestTimer(); set({ session: initialSession, incomingCtx: null }) },

  onIncoming: (p) => {
    set({ incomingCtx: { channelId: p.channelId, serverId: p.serverId, controllerUserId: p.fromUserId, controllerIdentity: p.controllerIdentity } })
    get().dispatch({ type: 'INCOMING_REQUEST', sessionId: p.sessionId, peerUserId: p.fromUserId, peerName: p.fromName, controllerIdentity: p.controllerIdentity })
  },

  approve: () => {
    const s = get().session
    const ctx = get().incomingCtx
    if (s.state !== 'pending' || !ctx || !s.sessionId) return
    get().dispatch({ type: 'APPROVED' })
    getSocket().emit('remote-control:decision', {
      sessionId: s.sessionId,
      accepted: true,
      channelId: ctx.channelId,
      serverId: ctx.serverId,
      controllerUserId: ctx.controllerUserId,
      controllerIdentity: ctx.controllerIdentity,
      controlleeUserId: '', // server pins this to the authed socket id
    })
  },

  deny: () => {
    const s = get().session
    const ctx = get().incomingCtx
    if (s.state !== 'pending' || !s.sessionId) return
    getSocket().emit('remote-control:decision', {
      sessionId: s.sessionId,
      accepted: false,
      channelId: ctx?.channelId ?? '',
      serverId: ctx?.serverId ?? '',
      controllerUserId: ctx?.controllerUserId ?? '',
      controllerIdentity: ctx?.controllerIdentity ?? '',
      controlleeUserId: '',
    })
    get().dispatch({ type: 'DENIED' })
  },

  request: ({ channelId, serverId, controlleeUserId, controlleeName, controllerIdentity }) => {
    getSocket().emit(
      'remote-control:request',
      { channelId, serverId, controlleeUserId, controllerIdentity },
      (res: { ok: boolean; sessionId?: string; reason?: string }) => {
        if (!res?.ok || !res.sessionId) return
        get().dispatch({ type: 'REQUEST', sessionId: res.sessionId, peerUserId: controlleeUserId, peerName: controlleeName, controllerIdentity })
        clearRequestTimer()
        const sid = res.sessionId
        requestTimer = setTimeout(() => {
          const s = get().session
          if (s.state === 'requested' && s.sessionId === sid) {
            useToastStore.getState().showToast({
              id: 'rc-timeout-' + sid,
              type: 'error',
              title: 'Remote control unavailable',
              body: "The other participant didn't respond — they may be on the web app or an older desktop version.",
            })
            get().end('no-response')
          }
        }, REQUEST_TIMEOUT_MS)
      },
    )
  },

  onDecision: (p) => {
    clearRequestTimer()
    const s = get().session
    if (s.state !== 'requested' || s.sessionId !== p.sessionId) return
    if (p.accepted) get().dispatch({ type: 'APPROVED' })
    else get().dispatch({ type: 'DENIED' })
  },

  sendInput: (targetIdentity, packet) => {
    const s = get().session
    const room = useLivekitStore.getState().room
    if (!room || s.state !== 'active' || !s.sessionId) return
    void publishControl(room, targetIdentity, s.sessionId, packet)
  },

  end: (reason) => {
    clearRequestTimer()
    const s = get().session
    if (s.state === 'idle' || s.state === 'ended') return
    if (s.sessionId && s.peerUserId) {
      getSocket().emit('remote-control:stop', { sessionId: s.sessionId, reason, notifyUserId: s.peerUserId })
    }
    get().dispatch({ type: 'END', reason })
  },
}))
